import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  Badge,
  Button,
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Columns3, ListTree, Plus, Rows3, Trash2 } from 'lucide-react';
import {
  taxonomyListsQueryOptions,
  useBulkDeleteTaxonomyLists,
  type TaxonomyListIndexEntry,
} from '@/entities/taxonomy-list';
import { TaxonomyStatusBadge } from '@/entities/taxonomy';
import { usePermissions } from '@/entities/user';
import { PageTransition } from '@/shared/ui/animations';
import {
  TaxonomyEmptyState,
  TaxonomyErrorState,
  TaxonomyNoResultsState,
} from '../components/TaxonomyStates/TaxonomyStates';
import { MonksFlowMarkIcon } from '@/shared/ui/Icons';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { ROUTES } from '@/shared/config/routes';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { ROW_WITHOUT_FOCUS_RING, redesignTableStyles } from '@/shared/ui/data-table';
import { formatShortDate } from '@/shared/utils/format';
import { AddStandardListsModal } from './components/AddStandardListsModal/AddStandardListsModal';
import { DeleteListDialog } from './components/DeleteListDialog/DeleteListDialog';
import { ListActionMenu } from './components/ListActionMenu/ListActionMenu';
import { ListSheet } from './components/ListSheet/ListSheet';

/** Placeholder rows/cells while the index loads, matching the Schemas tab. */
const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);
/** Two fewer without the select and actions columns a writer gets. */
const SKELETON_CELLS = Array.from({ length: 9 }, (_, i) => i);
const SKELETON_CELLS_WRITABLE = Array.from({ length: 11 }, (_, i) => i);

/**
 * Lists tab — the org's shared, reusable vocabularies (Region, Country,
 * Channel…). Unlike a field's inline options, a list is defined once and
 * referenced by many schemas, so updating it propagates everywhere.
 *
 * Chrome (header, tabs, content frame) lives in `TaxonomyLayout`. A list opens in a
 * side sheet over this tab, at its own URL so a link to it still works.
 */
export function TaxonomyLists() {
  const { t } = useTranslation('knowledge');
  // List writes are manage-guarded server-side; mirror that here. Opening a list
  // is a read, so the row stays navigable either way.
  const { canManageTaxonomy } = usePermissions();
  const navigate = useNavigate();
  const { listId } = useParams<{ listId?: string }>();
  const [createOpen, setCreateOpen] = useState(false);
  const [standardOpen, setStandardOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Owned by the page, not the row: deleting drops the row, and a Radix dialog
  // unmounted while open leaves `body { pointer-events: none }` behind, which
  // freezes the entire page.
  const [deleting, setDeleting] = useState<TaxonomyListIndexEntry | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery(taxonomyListsQueryOptions());
  const lists = useMemo<TaxonomyListIndexEntry[]>(() => query.data ?? [], [query.data]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return lists;
    return lists.filter((l) => l.name.toLowerCase().includes(term));
  }, [lists, debouncedSearch]);

  // A sheet opened from this page is one history entry: what it opens next replaces it, and closing
  // it steps back to the entry it was opened from. A deep-linked sheet has no such entry.
  const location = useLocation();
  const openedFromIndex =
    (location.state as { openedFromIndex?: boolean } | null)?.openedFromIndex === true;
  const openList = useCallback(
    (id: string) => {
      void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_LIST_DETAIL(id), {
        state: { openedFromIndex: true },
      });
    },
    [navigate],
  );
  const openInSheet = (id: string) => {
    if (!listId) {
      openList(id);
      return;
    }
    void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_LIST_DETAIL(id), {
      replace: true,
      state: location.state as unknown,
    });
  };

  // Cleared only once the new list's URL has arrived: clearing it with the navigation left one
  // render with neither set, which closed the sheet and opened it again.
  if (listId && createOpen) setCreateOpen(false);

  const closeSheet = () => {
    setCreateOpen(false);
    if (!listId) return;
    if (openedFromIndex) void navigate(-1);
    else void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_LISTS, { replace: true });
  };

  // Selection tracks only what's currently VISIBLE, so a filtered-out row can
  // never be deleted by a "select all" the user couldn't see.
  const visibleSelected = useMemo(
    () => filtered.filter((l) => selectedIds.has(l.id)),
    [filtered, selectedIds],
  );
  const allVisibleSelected = filtered.length > 0 && visibleSelected.length === filtered.length;

  // Functional updater, not `new Set(selectedIds)`: two ticks in the same batch
  // would both read the same snapshot and the second would drop the first.
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filtered.map((l) => l.id)));

  // `usageCount` on the index means the confirm can split these BEFORE calling,
  // rather than reporting the split afterwards.
  const selectedInUse = useMemo(
    () => visibleSelected.filter((l) => l.usageCount > 0),
    [visibleSelected],
  );
  const deletableSelected = useMemo(
    () => visibleSelected.filter((l) => l.usageCount === 0),
    [visibleSelected],
  );

  const bulkDelete = useBulkDeleteTaxonomyLists({
    onSuccess: (result) => {
      setBulkDeleteOpen(false);
      /**
       * Remove what was deleted, rather than replacing the selection with what
       * the server blocked.
       *
       * Confirm submits only the deletable ids, and the server can only report
       * `blocked` for ids it was sent — so an in-use row the dialog promised to
       * keep can never come back in that list, and replacing the selection with
       * it silently deselected every one of them.
       */
      const deleted = new Set(result.deletedIds);
      setSelectedIds((prev) => new Set([...prev].filter((id) => !deleted.has(id))));
    },
  });

  const isEmpty = !query.isLoading && !query.isError && lists.length === 0;
  /**
   * Filtered to nothing is NOT the same as empty, and gating only on the
   * unfiltered total meant a search matching no rows rendered a bare column
   * header over an empty body — with no `role="status"` either, so assistive
   * technology was told nothing at all.
   */
  const showNoResults =
    !query.isLoading && !query.isError && lists.length > 0 && filtered.length === 0;

  return (
    <>
      <PageTransition variant="fade" className="flex min-h-0 flex-1 flex-col">
        {!isEmpty && !query.isError && (
          <p className="px-section pt-3 text-sm text-muted-foreground">
            {t('taxonomy.taxonomyLists.sharedModelNote')}
          </p>
        )}
        {!isEmpty && (
          <div className="flex items-center justify-between gap-3 px-section py-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('taxonomy.taxonomyLists.searchLists')}
              aria-label={t('taxonomy.taxonomyLists.searchLists2')}
              className="w-72"
            />
            <div className="flex items-center gap-2">
              {canManageTaxonomy && visibleSelected.length > 0 && (
                <Button
                  variant="destructive"
                  size="mini"
                  className="h-8"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="size-3.5" />
                  {t('taxonomy.taxonomyLists.deleteLists', { count: visibleSelected.length })}
                </Button>
              )}
              {canManageTaxonomy && (
                <>
                  <Button
                    variant="outline"
                    size="mini"
                    className="h-8"
                    onClick={() => setStandardOpen(true)}
                    title={t('taxonomy.taxonomyLists.chooseWhichMonksflowStandardLists')}
                  >
                    <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                    {t('taxonomy.taxonomyLists.addStandardLists')}
                  </Button>
                  <Button size="mini" className="h-8" onClick={() => setCreateOpen(true)}>
                    <Plus className="size-3.5" />
                    {t('taxonomy.taxonomyLists.newList')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {query.isError ? (
          <TaxonomyErrorState
            title={t('taxonomy.taxonomyStates.couldnTLoadLists')}
            onRetry={() => void query.refetch()}
          />
        ) : isEmpty ? (
          <TaxonomyEmptyState
            icon={<ListTree className="size-6 text-muted-foreground" aria-hidden />}
            title={t('taxonomy.taxonomyLists.noListsYet')}
            description={t('taxonomy.taxonomyLists.listReusableSetOptionsLike')}
          >
            {canManageTaxonomy && (
              <div className="flex items-center gap-2">
                <Button size="mini" onClick={() => setStandardOpen(true)}>
                  <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                  {t('taxonomy.taxonomyLists.addStandardLists')}
                </Button>
                <Button variant="outline" size="mini" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-3.5" />
                  {t('taxonomy.taxonomyLists.createYourOwn')}
                </Button>
              </div>
            )}
          </TaxonomyEmptyState>
        ) : showNoResults ? (
          <TaxonomyNoResultsState
            title={t('taxonomy.taxonomyStates.noListsFound')}
            description={t('taxonomy.taxonomyStates.noListsMatchYourSearch')}
            onClear={() => setSearch('')}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto px-section pb-section">
            {/* Platform Datatable primitives rather than a hand-rolled table, so
                header styling, row height, hover and borders match Projects and
                the Schemas tab instead of drifting on their own. */}
            <Datatable
              aria-busy={query.isLoading || undefined}
              className={redesignTableStyles.table}
            >
              <DatatableHeader>
                <DatatableRow className={ROW_WITHOUT_FOCUS_RING}>
                  {canManageTaxonomy && (
                    <DatatableHead scope="col" className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleAllVisible}
                        aria-label={
                          allVisibleSelected
                            ? t('taxonomy.taxonomyLists.clearSelection')
                            : t('taxonomy.taxonomyLists.selectAllLists')
                        }
                      />
                    </DatatableHead>
                  )}
                  <DatatableHead scope="col">{t('taxonomy.taxonomyLists.name')}</DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyLists.type')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-20">
                    {t('taxonomy.taxonomyLists.version')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyLists.status')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyLists.scope')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-24">
                    {t('taxonomy.taxonomyLists.columns')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-24">
                    {t('taxonomy.taxonomyLists.options')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-28">
                    {t('taxonomy.taxonomyLists.used')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-40">
                    {t('taxonomy.taxonomyLists.lastModified')}
                  </DatatableHead>
                  {canManageTaxonomy && (
                    <DatatableHead scope="col" className="w-13">
                      <span className="sr-only">{t('taxonomy.taxonomyLists.actions')}</span>
                    </DatatableHead>
                  )}
                </DatatableRow>
              </DatatableHeader>
              <DatatableBody>
                {query.isLoading
                  ? SKELETON_ROWS.map((i) => (
                      <DatatableRow key={i} className="h-[72px]">
                        {(canManageTaxonomy ? SKELETON_CELLS_WRITABLE : SKELETON_CELLS).map((c) => (
                          <DatatableCell key={c}>
                            <Skeleton className="h-4 w-full" />
                          </DatatableCell>
                        ))}
                      </DatatableRow>
                    ))
                  : filtered.map((l) => (
                      <DatatableRow
                        key={l.id}
                        interactive
                        className={`h-[72px] ${ROW_WITHOUT_FOCUS_RING}`}
                        // A double-click's second click would open the same list a second time.
                        onClick={(e) => e.detail < 2 && openList(l.id)}
                      >
                        {canManageTaxonomy && (
                          <DatatableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(l.id)}
                              onCheckedChange={() => toggleRow(l.id)}
                              aria-label={t('taxonomy.taxonomyLists.selectList', { name: l.name })}
                            />
                          </DatatableCell>
                        )}
                        <DatatableCell>
                          <span className="text-sm font-medium text-foreground">{l.name}</span>
                          {l.description && (
                            <div className="truncate text-xs text-muted-foreground">
                              {l.description}
                            </div>
                          )}
                        </DatatableCell>
                        {/* Provenance as its own column rather than a chip crowding
                            the name — every row answers it, so it reads as an
                            attribute of the list, not an exception on some. */}
                        <DatatableCell>
                          {l.libraryKey ? (
                            <Badge
                              variant="default"
                              roundness="full"
                              className={microInteraction.statusBadge}
                            >
                              <MonksFlowMarkIcon className="mr-1 h-3 w-[1.1rem]" />
                              {t('taxonomy.taxonomyLists.standard')}
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              roundness="full"
                              className={microInteraction.statusBadge}
                            >
                              {t('taxonomy.taxonomyLists.custom')}
                            </Badge>
                          )}
                        </DatatableCell>
                        <DatatableCell>
                          {l.currentVersionNumber != null ? (
                            <span className="text-sm text-foreground">
                              {t('taxonomy.common.versionLabel', {
                                number: l.currentVersionNumber,
                              })}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </DatatableCell>
                        <DatatableCell>
                          {l.status ? (
                            <TaxonomyStatusBadge status={l.status} />
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </DatatableCell>
                        <DatatableCell>
                          <span className="text-sm text-foreground">
                            {l.scope === 'organization'
                              ? t('taxonomy.taxonomyLists.organization')
                              : t('taxonomy.taxonomyLists.workspace')}
                          </span>
                        </DatatableCell>
                        <DatatableCell>
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                            <Columns3 className="size-4 text-muted-foreground" aria-hidden />
                            {l.columnCount}
                          </span>
                        </DatatableCell>
                        <DatatableCell>
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                            <Rows3 className="size-4 text-muted-foreground" aria-hidden />
                            {l.rowCount}
                          </span>
                        </DatatableCell>
                        {/* Makes "is this safe to delete?" answerable at a glance —
                            0 is exactly the set bulk delete can act on. */}
                        <DatatableCell>
                          {l.usageCount > 0 ? (
                            <span className="text-sm text-foreground">
                              {t('taxonomy.taxonomyLists.schemaCount', { count: l.usageCount })}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {t('taxonomy.taxonomyLists.unused')}
                            </span>
                          )}
                        </DatatableCell>
                        <DatatableCell>
                          <span className="text-sm text-foreground">
                            {formatShortDate(l.updatedAt)}
                          </span>
                        </DatatableCell>
                        {/* Duplicate and delete are all it holds, so it has
                            nothing to offer a viewer who can do neither. */}
                        {canManageTaxonomy && (
                          <DatatableCell className="text-right">
                            <ListActionMenu list={l} onDelete={() => setDeleting(l)} />
                          </DatatableCell>
                        )}
                      </DatatableRow>
                    ))}
              </DatatableBody>
            </Datatable>
          </div>
        )}
      </PageTransition>

      {bulkDeleteOpen && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setBulkDeleteOpen(false)}
          title={t('taxonomy.taxonomyLists.deleteListsTitle', {
            count: deletableSelected.length,
          })}
          className="flex max-h-[calc(100dvh-64px)] flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body className="flex min-h-0 flex-col gap-3 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.taxonomyLists.everyVersionAllOptionsGo')}
            </p>
            <ul className="flex flex-col gap-0.5">
              {visibleSelected
                .filter((l) => l.usageCount === 0)
                .map((l) => (
                  <li key={l.id} className="truncate text-xs text-muted-foreground">
                    {l.name}
                    <span className="ml-1 opacity-70">
                      ({t('taxonomy.taxonomyLists.optionCount', { count: l.rowCount })})
                    </span>
                  </li>
                ))}
            </ul>
            {selectedInUse.length > 0 && (
              <div className="flex flex-col gap-1 rounded-md border border-border-light bg-surface p-2.5">
                <span className="text-xs font-medium text-foreground">
                  {t('taxonomy.taxonomyLists.willBeKeptStillUsed', { count: selectedInUse.length })}
                </span>
                <ul className="flex flex-col gap-0.5">
                  {selectedInUse.map((l) => (
                    <li key={l.id} className="truncate text-xs text-muted-foreground">
                      {l.name}
                      <span className="ml-1 opacity-70">
                        ({t('taxonomy.taxonomyLists.schemaCount', { count: l.usageCount })})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyLists.deleteLists', { count: deletableSelected.length }),
              onClick: () => bulkDelete.mutate(deletableSelected.map((l) => l.id)),
              disabled: bulkDelete.isPending || deletableSelected.length === 0,
              loading: bulkDelete.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyLists.cancel'),
              onClick: () => setBulkDeleteOpen(false),
            }}
          />
        </FormDialog>
      )}

      {deleting && <DeleteListDialog list={deleting} onClose={() => setDeleting(null)} />}

      {standardOpen && <AddStandardListsModal open onClose={() => setStandardOpen(false)} />}

      {/* Mounted for both modes, so Create turns into the editor without the sheet closing. */}
      <ListSheet
        open={createOpen || Boolean(listId)}
        listId={listId}
        onClose={closeSheet}
        onOpenList={openInSheet}
      />
    </>
  );
}

TaxonomyLists.displayName = 'TaxonomyLists';
